import { useState } from 'react';
import {
  Box, Button, Flex, Input, Menu, MenuButton, MenuItem, MenuList, Portal, Text,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Trans, useTranslation } from 'react-i18next';
import { ColorPicker } from '../categories/CategoriesPage';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { FALLBACK_GOAL_COLOR, formatAmount, monthsUntil } from '../../utils/goals';
import GoalAccountsPicker from './GoalAccountsPicker';
import { GoalSuggestionChips } from './GoalSuggestions';
import { GOAL_ICONS } from './GoalIcon';

const MODE_OPTIONS = [
  { value: 'target_date', labelKey: 'modes.target_date' },
  { value: 'monthly_contribution', labelKey: 'modes.monthly_contribution' },
];

function FieldRow({ label, children, align = 'center' }) {
  return (
    <Flex py="8px" align={align} gap={3}>
      <Text fontSize="sm" color="redesign.text.muted" w="130px" flexShrink={0} pt={align === 'flex-start' ? 2 : 0}>
        {label}
      </Text>
      <Box flex={1} minW={0}>{children}</Box>
    </Flex>
  );
}

const today = () => new Date().toISOString().split('T')[0];

function initialValues(goal, currency, draft) {
  return {
    name: goal?.name || draft?.name || '',
    color: goal?.color || draft?.color || '',
    icon: goal?.icon || draft?.icon || 'target',
    target_amount: goal?.target_amount ?? '',
    currency: goal?.currency || currency || '',
    saving_mode: goal?.saving_mode || 'target_date',
    start_date: goal?.start_date || today(),
    target_date: goal?.target_date || '',
    monthly_contribution: goal?.monthly_contribution ?? '',
    account_ids: (goal?.accounts || []).map((a) => a.account_id),
  };
}

// The same rules the backend enforces, checked before sending so the button
// only lights up for a goal the API will accept.
function isComplete(v) {
  const target = parseFloat(v.target_amount);
  if (!v.name.trim() || !(target > 0) || !v.currency || !v.start_date) return false;
  if (v.account_ids.length === 0) return false;
  if (v.saving_mode === 'target_date') return Boolean(v.target_date) && v.target_date > v.start_date;
  return parseFloat(v.monthly_contribution) > 0;
}

function PlanPreview({ values }) {
  const target = parseFloat(values.target_amount);
  if (!(target > 0) || !values.currency) return null;
  const amount = (n) => `${values.currency} ${formatAmount(n)}`;

  let content = null;
  if (values.saving_mode === 'target_date' && values.target_date > values.start_date) {
    const from = values.start_date > today() ? new Date(`${values.start_date}T00:00:00`) : new Date();
    const months = Math.max(1, monthsUntil(values.target_date, from));
    content = (
      <Trans ns="goals" i18nKey="preview.perMonth"
        values={{ amount: amount(Math.ceil(target / months)), count: months }}
        components={{ strong: <strong /> }} />
    );
  } else if (values.saving_mode === 'monthly_contribution' && parseFloat(values.monthly_contribution) > 0) {
    const monthly = parseFloat(values.monthly_contribution);
    content = (
      <Trans ns="goals" i18nKey="preview.months"
        values={{ amount: amount(monthly), count: Math.ceil(target / monthly) }}
        components={{ strong: <strong /> }} />
    );
  }
  if (!content) return null;

  return (
    <Box mt={2} px={3} py={2} borderRadius="lg" bg="redesign.bg.card">
      <Text fontSize="sm" color="redesign.heading">{content}</Text>
    </Box>
  );
}

/**
 * Create and edit a goal. The saving plan is one of two inputs — a target date
 * or a monthly amount — and the other is derived, so only one is ever shown.
 */
export default function GoalForm({ goal, draft, accounts, onSubmit, onCancel, saving }) {
  const { t } = useTranslation('goals');
  const { activeCurrencies, displayCurrency } = useDisplayCurrency();
  const { homeCurrency } = usePreferences();
  const [values, setValues] = useState(() =>
    initialValues(goal, homeCurrency || displayCurrency || activeCurrencies[0], draft)
  );
  const set = (field) => (value) => setValues((prev) => ({ ...prev, [field]: value }));
  const currencies = activeCurrencies.includes(values.currency) || !values.currency
    ? activeCurrencies
    : [values.currency, ...activeCurrencies];

  return (
    <Box>
      {!goal && (
        <Box pb={3} mb={2} borderBottom="1px solid" borderColor="redesign.border">
          <Text textStyle="microLabel" mb={2}>
            {t('empty.suggestedTitle')}
          </Text>
          <GoalSuggestionChips
            onPick={(item) => setValues((prev) => ({
              ...prev, name: item.name, icon: item.icon, color: item.color,
            }))}
          />
        </Box>
      )}
      <FieldRow label={t('fields.name')}>
        <Input
          size="sm"
          value={values.name}
          onChange={(e) => set('name')(e.target.value)}
          placeholder={t('fields.namePlaceholder')}
          autoFocus={!goal}
        />
      </FieldRow>

      <FieldRow label={t('fields.color')}>
        <ColorPicker value={values.color || FALLBACK_GOAL_COLOR} onChange={set('color')} />
      </FieldRow>

      <FieldRow label={t('fields.icon')}>
        <Flex wrap="wrap" gap={1}>
          {Object.entries(GOAL_ICONS).map(([key, Icon]) => {
            const isSelected = values.icon === key;
            return (
              <Flex
                key={key}
                as="button"
                type="button"
                aria-label={t(`icons.${key}`)}
                aria-pressed={isSelected}
                onClick={() => set('icon')(key)}
                w="30px"
                h="30px"
                align="center"
                justify="center"
                borderRadius="md"
                border="1px solid"
                borderColor={isSelected ? 'redesign.heading' : 'redesign.border'}
                color={isSelected ? (values.color || FALLBACK_GOAL_COLOR) : 'redesign.text.muted'}
                _hover={{ bg: 'redesign.bg.hover' }}
              >
                <Icon size={15} />
              </Flex>
            );
          })}
        </Flex>
      </FieldRow>

      <FieldRow label={t('fields.targetAmount')}>
        <Flex gap={2}>
          <Input
            size="sm"
            type="number"
            min={0}
            value={values.target_amount}
            onChange={(e) => set('target_amount')(e.target.value)}
          />
          {/* The app's own dropdown, not a native <select>: the house style asks
              for it, and a native pop-up is drawn by the browser, which some
              embedded browsers never open. Portalled so the panel's scroll
              container cannot clip the list. */}
          <Menu placement="bottom-end" isLazy>
            <MenuButton
              as={Button}
              size="sm"
              w="110px"
              flexShrink={0}
              variant="outline"
              fontWeight="500"
              rightIcon={<ChevronDownIcon />}
              aria-label={t('fields.currency')}
            >
              {values.currency}
            </MenuButton>
            <Portal>
              <MenuList minW="140px" maxH="240px" overflowY="auto" overflowX="hidden" zIndex="popover">
                {currencies.map((c) => (
                  <MenuItem
                    key={c}
                    fontSize="sm"
                    fontWeight={c === values.currency ? 600 : 400}
                    bg={c === values.currency ? 'redesign.bg.accent' : undefined}
                    onClick={() => set('currency')(c)}
                  >
                    {c}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        </Flex>
      </FieldRow>

      <FieldRow label={t('fields.savingMode')}>
        <Flex bg="redesign.bg.sunken" border="1px solid" borderColor="redesign.border" borderRadius="lg" p={1} gap={1} role="radiogroup" w="fit-content">
          {MODE_OPTIONS.map((opt) => {
            const isActive = values.saving_mode === opt.value;
            return (
              <Box
                key={opt.value}
                as="button"
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => set('saving_mode')(opt.value)}
                px={3}
                py="5px"
                borderRadius="md"
                fontSize="sm"
                fontWeight={isActive ? 600 : 500}
                bg={isActive ? 'redesign.bg.raised' : 'transparent'}
                color={isActive ? 'redesign.heading' : 'redesign.text.muted'}
                boxShadow={isActive ? 'sm' : 'none'}
              >
                {t(opt.labelKey)}
              </Box>
            );
          })}
        </Flex>
      </FieldRow>

      <FieldRow label={t('fields.startDate')}>
        <Input size="sm" type="date" value={values.start_date} onChange={(e) => set('start_date')(e.target.value)} />
      </FieldRow>

      {values.saving_mode === 'target_date' ? (
        <FieldRow label={t('fields.targetDate')}>
          <Input size="sm" type="date" value={values.target_date} min={values.start_date} onChange={(e) => set('target_date')(e.target.value)} />
        </FieldRow>
      ) : (
        <FieldRow label={t('fields.monthlyContribution')}>
          <Input size="sm" type="number" min={0} value={values.monthly_contribution} onChange={(e) => set('monthly_contribution')(e.target.value)} />
        </FieldRow>
      )}

      <PlanPreview values={values} />

      {!goal && (
        <Box py="8px" mt={2}>
          <Text fontSize="sm" color="redesign.text.muted" mb={2}>{t('fields.accounts')}</Text>
          <GoalAccountsPicker accounts={accounts} selectedIds={values.account_ids} onChange={set('account_ids')} />
        </Box>
      )}

      <Flex mt={5} gap={2}>
        <Button
          variant="primary"
          flex={1}
          size="sm"
          onClick={() => onSubmit(values)}
          isLoading={saving}
          isDisabled={!isComplete(values)}
        >
          {goal ? t('buttons.save') : t('buttons.create')}
        </Button>
        <Button variant="outline" size="sm" onClick={onCancel}>
          {t('buttons.cancel')}
        </Button>
      </Flex>
    </Box>
  );
}
